import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/user.service';
import { useAuth } from '../../store/auth.store';
import StatCard from '../../components/cards/StatCard';
import Badge from '../../components/common/Badge';

declare global {
  interface Window {
    TradingView: any;
  }
}

const UserDashboardSkeleton = () => (
  <div className="space-y-10 animate-pulse">
    <div className="h-64 bg-slate-900/50 rounded-[2.5rem] border border-white/5"></div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-48 bg-slate-900/50 rounded-[2rem] border border-white/5"></div>
      ))}
    </div>
  </div>
);

const UserDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalTrades: 0,
    wins: 0,
    losses: 0,
    calculatedBalance: 0
  });
  const [profile, setProfile] = useState<any>(null);
  const [btcAddress, setBtcAddress] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tradingsRes, profRes, btcRes, balanceRes] = await Promise.all([
          userService.getTradings(),
          userService.getProfile(),
          userService.getBtcAddress(),
          userService.getBalance()
        ]);

        // 1. Process trade logs for count stats
        const trades = Array.isArray(tradingsRes) ? tradingsRes : (tradingsRes?.data || []);
        const activeTrades = trades.filter((t: any) => !t.deletedAt);
        
        const wins = activeTrades.filter((t: any) => t.result === 'WIN');
        const losses = activeTrades.filter((t: any) => t.result === 'LOSS');

        // 2. Use balance from API
        const fetchedBalance = Number(balanceRes?.data?.balance || 0);

        setStats({
          totalTrades: activeTrades.length,
          wins: wins.length,
          losses: losses.length,
          calculatedBalance: fetchedBalance
        });

        setProfile(profRes.data || profRes);
        setBtcAddress(btcRes?.data?.btcAddress || btcRes?.data?.address || btcRes?.btcAddress || btcRes?.address || '');
      } catch (err) {
        console.error("Dashboard sync error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && chartContainerRef.current) {
      const scriptId = 'tradingview-widget-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => initTradingView();
        document.head.appendChild(script);
      } else {
        initTradingView();
      }
    }

    function initTradingView() {
      if (window.TradingView && chartContainerRef.current) {
        new window.TradingView.widget({
          "autosize": true,
          "symbol": "BINANCE:BTCUSDT",
          "interval": "15",
          "timezone": "UTC",
          "theme": "dark",
          "style": "2",
          "locale": "en",
          "toolbar_bg": "#020617",
          "enable_publishing": false,
          "hide_top_toolbar": true,
          "hide_legend": true,
          "save_image": false,
          "container_id": "tradingview_terminal",
          "backgroundColor": "#020617",
          "gridColor": "rgba(139, 92, 246, 0.05)",
          "withdateranges": false,
          "hide_side_toolbar": true,
          "details": false,
          "hotlist": false,
          "calendar": false,
          "studies": ["MASimple@tv-basicstudies"],
          "overrides": {
            "mainSeriesProperties.style": 2,
            "mainSeriesProperties.lineStyle.color": "#8b5cf6",
            "mainSeriesProperties.lineStyle.linewidth": 4,
            "paneProperties.background": "#020617",
            "paneProperties.vertGridProperties.color": "rgba(139, 92, 246, 0.05)",
            "paneProperties.horzGridProperties.color": "rgba(139, 92, 246, 0.05)",
            "scalesProperties.textColor": "#64748b",
            "mainSeriesProperties.candleStyle.upColor": "#8b5cf6",
            "mainSeriesProperties.candleStyle.downColor": "#4c1d95"
          }
        });
      }
    }
  }, [loading]);

  if (loading) return <UserDashboardSkeleton />;

  const isVerified = profile?.verified === true || profile?.verified === 1;
  const isAgaMember = user?.aga === 1;

  const handleCopyBtc = () => {
    if (btcAddress) {
      navigator.clipboard.writeText(btcAddress);
      alert("Master Node Hash Copied");
    }
  };

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 relative overflow-hidden bg-slate-950 rounded-[3rem] p-12 border border-white/5 shadow-2xl group min-h-[320px] flex flex-col justify-between scanline-effect">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-slate-950 to-indigo-950/30"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
               <div className="flex items-center space-x-3">
                  <div className="inline-flex items-center space-x-3 bg-slate-900/80 border border-white/10 px-5 py-2 rounded-full backdrop-blur-xl relative">
                    <span className={`w-2 h-2 rounded-full ${isVerified ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]' : 'bg-amber-500 shadow-[0_0_10px_#f59e0b]'}`}></span>
                    <span className="text-white text-[10px] font-black uppercase tracking-[0.3em]">{isVerified ? 'Elite Account' : 'Action Required'}</span>
                  </div>
                  {isAgaMember && (
                    <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-full border border-amber-400/50 shadow-[0_0_20px_rgba(245,158,11,0.3)]">
                      <span className="text-[10px] font-black text-slate-950 uppercase tracking-widest">AGA SPECIAL</span>
                    </div>
                  )}
               </div>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-white uppercase tracking-tighter leading-none relative">
              <span className="opacity-60 block text-2xl mb-1 tracking-tight">Access Granted,</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-white to-indigo-400 drop-shadow-[0_10px_20px_rgba(139,92,246,0.3)]">{user?.fname || 'USER'}</span>.
            </h2>
          </div>
          <div className="relative z-10 mt-12 flex flex-wrap gap-5">
             <button onClick={() => navigate('/tradings')} className="px-10 py-5 bg-purple-600 hover:bg-purple-500 text-white rounded-[2rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all shadow-[0_20px_40px_-10px_rgba(139,92,246,0.4)] active:scale-95 border border-purple-400/30">Start Trading</button>
             <button onClick={() => navigate('/withdrawals')} className="px-10 py-5 bg-slate-900/80 hover:bg-slate-900 text-slate-300 border border-white/5 rounded-[2rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all active:scale-95">Funds Center</button>
          </div>
        </div>

        <div className="bg-[#03040c] backdrop-blur-3xl rounded-[3rem] p-10 border border-purple-500/10 shadow-2xl relative overflow-hidden flex flex-col justify-between group">
           <div className="absolute inset-0 bg-purple-600/5 blur-3xl pointer-events-none"></div>
           <div>
             <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.4em] mb-10 flex items-center">
                <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Deposit Master Node
             </h3>
             <div className="p-6 bg-slate-950 border border-white/5 rounded-3xl group-hover:border-purple-500/20 transition-all">
                <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-3">Target Broadcast Address (BTC)</p>
                <p className="text-xs font-mono text-purple-400 font-bold break-all leading-relaxed">
                  {btcAddress || 'SYNCHRONIZING...'}
                </p>
             </div>
           </div>
           <button onClick={handleCopyBtc} disabled={!btcAddress} className="w-full mt-10 py-5 bg-purple-600/10 hover:bg-purple-600 text-purple-400 hover:text-white border border-purple-500/20 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all shadow-xl shadow-purple-600/5 active:scale-95">Copy Node Hash</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label="Live Capital" value={`$${stats.calculatedBalance.toFixed(2)}`} color="purple" icon={<path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />} />
        <StatCard label="Active Ops" value={stats.totalTrades} color="blue" icon={<path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />} />
        <StatCard label="Won" value={stats.wins} color="green" icon={<path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />} />
        <StatCard label="Lost" value={stats.losses} color="red" icon={<path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />} />
      </div>

      <div className="relative group">
        <div className="absolute inset-0 bg-purple-600/10 blur-[100px] pointer-events-none opacity-40"></div>
        <div className="relative glass-panel rounded-[3rem] overflow-hidden border border-purple-500/20 shadow-[0_0_80px_rgba(139,92,246,0.15)] scanline-effect">
          <div className="px-10 py-8 border-b border-white/5 flex items-center justify-between bg-[#020617]/80 backdrop-blur-3xl">
             <div className="flex items-center space-x-6">
                <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center border border-purple-500/40">
                   <svg className="w-7 h-7 text-purple-400 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1 1V4z" /></svg>
                </div>
                <div>
                   <h3 className="text-2xl font-black text-white uppercase tracking-tighter">BTC / USDT</h3>
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] mt-1.5">Binance Liquidity Stream | v3.1</p>
                </div>
             </div>
          </div>
          <div className="h-[650px] w-full bg-[#020617] relative">
             <div id="tradingview_terminal" className="w-full h-full" ref={chartContainerRef}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;